import {
  BarChart3,
  Clock,
  Database,
  Download,
  Info,
  MessageSquare,
  ShieldAlert,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FeatureCoach } from '../../components/FeatureCoach/FeatureCoach';
import { Tooltip } from '../../components/Tooltip';
import { useTheme } from '../../contexts/ThemeContext';
import {
  AnalyticsConnectTourBody,
  analyticsMission,
  GuidedTourCard,
  useGuidedTour,
} from '../../features/atlas';
import DailyTimeChart from '../../features/dashboard/DailyTimeChart';
import DeveloperTimeChart from '../../features/dashboard/DeveloperTimeChart';
import { DoneIssuesStatCard } from '../../features/analytics/DoneIssuesStatCard';
import TaskTypeDistributionChart from '../../features/dashboard/TaskTypeDistributionChart';
import DatabaseUpdater from '../../features/worklog/DatabaseUpdater';
import WorkLogTable from '../../features/worklog/WorkLogTable';
import {
  AnalyticsPeriod,
  decodeLegacyDateRange,
  decodePeriod,
  encodePeriod,
  PeriodPicker,
  resolvePeriodRange,
  usePeriodOptions,
} from '../../features/analytics/PeriodPicker';
import { QualityTab } from './tabs/QualityTab';
import { EstimationTrendCard } from '../../features/analytics/estimation/EstimationTrendCard';
import { EstimationUnitProvider } from '../../features/analytics/estimation/estimationUnit';
import { EstimationUnitToggle } from '../../features/analytics/estimation/EstimationUnitToggle';
import { EstimationDetailBlock } from '../../features/analytics/estimation/EstimationDetailBlock';
import { EstimationByPeriodTable } from '../../features/analytics/estimation/EstimationByPeriodTable';
import {
  ApiService,
  AnalyticsDateScope,
  AnalyticsDoneCountResponse,
  EstimationAccuracyDetailResponse,
  EstimationAccuracyResponse,
  EstimationDetailPeriodType,
} from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { JiraService } from '../../services/jira';
import { getApiBaseUrl } from '../../shared/config';
import { notifyJiraWorklogIssuesSynced } from '../../shared/jiraWorklogIssuesSynced';
import { useFullUpdateSpotlight, usePostUpdateJourney, useWorkLogData } from '../../shared/hooks';
import { useSetupStatus } from '../../shared/hooks/useSetupStatus';
import { useEstimationSettings } from '../../shared/hooks/useEstimationSettings';
import {
  isOrganizationOnboardingComplete,
  type PostUpdateJourneyStep,
} from '../../shared/onboarding/onboardingState';
import { DateRange, WorkLogEntry } from '../../shared/types';

const JIRA_BROWSE = 'https://hrsinternational.atlassian.net/browse/';

const ANALYTICS_PERIOD_STORAGE_PREFIX = 'workAnalytics.analyticsPeriod.v1:';
function getAnalyticsPeriodStorageKey(productId: string) {
  return `${ANALYTICS_PERIOD_STORAGE_PREFIX}${productId}`;
}

function analyticsScopeFromRange(dr: DateRange | null | undefined): AnalyticsDateScope | undefined {
  if (!dr?.startDate || !dr?.endDate) return undefined;
  return {
    startDate: dr.startDate,
    endDate: dr.endDate,
    ...(dr.fixVersion ? { fixVersion: dr.fixVersion } : {}),
  };
}

type AnalyticsTab = 'overview' | 'details' | 'estimation' | 'quality';

export const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentProduct, currentOrg } = useAuth();
  const productId = currentProduct?.id;
  const setupStatus = useSetupStatus();
  const { theme } = useTheme();
  const databaseSectionRef = useRef<HTMLDivElement>(null);
  const fullUpdateCoachRef = useRef<HTMLDivElement>(null);

  const { data: estimationSettings } = useEstimationSettings();
  const targetDeviationPct = estimationSettings?.settings.targetDeviationPct ?? 10;

  // Deep-link support: scroll Database Management section into view + pulse.
  useEffect(() => {
    if (location.hash !== '#analytics-database-anchor') return;
    const target = document.getElementById('analytics-database-anchor');
    if (!target) return;
    const HEADER_OFFSET = 88;
    let attempt = 0;
    const tryScroll = () => {
      const node = document.getElementById('analytics-database-anchor');
      if (!node) {
        if (attempt++ < 20) window.setTimeout(tryScroll, 80);
        return;
      }
      const rect = node.getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + rect.top - HEADER_OFFSET,
        behavior: 'smooth',
      });
      node.classList.add('analytics-database-anchor-pulse');
      window.setTimeout(() => {
        node.classList.remove('analytics-database-anchor-pulse');
      }, 3000);
    };
    tryScroll();
  }, [location.hash]);

  /**
   * Generic deep-link scroll: when the URL has a `#section-id` whose id is
   * rendered anywhere inside the current tab, scroll it into view. Gives us
   * one canonical path for jumps like `/analytics?tab=quality#dynamics-section`
   * without every new feature owning its own effect.
   */
  useEffect(() => {
    const hash = location.hash.replace(/^#/, '');
    if (!hash) return;
    if (hash === 'analytics-database-anchor') return; // handled above
    const HEADER_OFFSET = 88;
    let attempt = 0;
    const tryScroll = () => {
      const node = document.getElementById(hash);
      if (!node) {
        if (attempt++ < 30) window.setTimeout(tryScroll, 80);
        return;
      }
      const rect = node.getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + rect.top - HEADER_OFFSET,
        behavior: 'smooth',
      });
    };
    tryScroll();
  }, [location.hash]);
  const postUpdateJourneyCoachRef = useRef<HTMLDivElement>(null);
  const analyticsTourApi = useGuidedTour({ mission: analyticsMission });
  const atlasAnalyticsActive =
    analyticsTourApi.hydrated && !analyticsTourApi.isTerminal && analyticsTourApi.step !== null;

  const [jiraService] = useState(
    () =>
      new JiraService({
        apiUrl: getApiBaseUrl(),
        username: '',
        apiToken: '',
      })
  );

  // Unified period (drives every tab). Hydrated from URL on mount.
  const [period, setPeriodState] = useState<AnalyticsPeriod | null>(() => {
    const raw = searchParams.get('period');
    const decoded = decodePeriod(raw);
    if (decoded) return decoded;
    const legacy = decodeLegacyDateRange(
      searchParams.get('startDate'),
      searchParams.get('endDate')
    );
    return legacy;
  });

  // Persist period to URL when it changes (shareable links).
  const applyPeriodToUrl = useCallback(
    (next: AnalyticsPeriod | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete('startDate');
          params.delete('endDate');
          if (next) params.set('period', encodePeriod(next));
          else params.delete('period');
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPeriod = useCallback(
    (next: AnalyticsPeriod | null) => {
      if (next === null && productId) {
        try {
          localStorage.removeItem(getAnalyticsPeriodStorageKey(productId));
        } catch {
          // ignore
        }
      }
      setPeriodState(next);
      applyPeriodToUrl(next);
    },
    [applyPeriodToUrl, productId]
  );

  // Mirror non-null period into localStorage. Removal on Clear is only in setPeriod(null) to avoid
  // a mount-time race with useLayoutEffect hydration.
  useEffect(() => {
    if (!productId || !period) return;
    try {
      localStorage.setItem(getAnalyticsPeriodStorageKey(productId), encodePeriod(period));
    } catch {
      // ignore
    }
  }, [productId, period]);

  // URL wins over cache; if no ?period= (and no legacy range), rehydrate from localStorage.
  const periodInUrl = searchParams.get('period');
  const legacyStart = searchParams.get('startDate');
  const legacyEnd = searchParams.get('endDate');
  const hasPeriodInUrl = Boolean(
    (periodInUrl && decodePeriod(periodInUrl)) || (legacyStart && legacyEnd)
  );
  useLayoutEffect(() => {
    if (!productId || hasPeriodInUrl) return;
    const raw = (() => {
      try {
        return localStorage.getItem(getAnalyticsPeriodStorageKey(productId));
      } catch {
        return null;
      }
    })();
    const fromStore = decodePeriod(raw);
    if (fromStore) setPeriod(fromStore);
  }, [productId, hasPeriodInUrl, setPeriod]);

  const {
    options: periodOptions,
    loading: periodOptionsLoading,
    sprintPickerMeta,
  } = usePeriodOptions(productId);

  // Resolve period → concrete (startDate, endDate) used by all server queries.
  const resolvedPeriod = useMemo(() => {
    if (!period) return null;
    return resolvePeriodRange(period, periodOptions);
  }, [period, periodOptions]);

  const dateFilterRange = useMemo((): DateRange | null => {
    if (!resolvedPeriod) return null;
    return {
      startDate: resolvedPeriod.startDate,
      endDate: resolvedPeriod.endDate,
    };
  }, [resolvedPeriod]);

  const {
    data,
    error,
    loadDataByDateRange,
    loadAllData,
    tableEntries,
    tableTotal,
    tablePage,
    tablePageSize,
    setTablePageSize,
    tablePageSizeOptions,
    tableLoading,
    loadWorkLogPage,
  } = useWorkLogData(jiraService);

  const [filteredData, setFilteredData] = useState<WorkLogEntry[]>([]);
  const [activeTab, setActiveTab] = useState<AnalyticsTab>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'details' || tab === 'estimation' || tab === 'quality') return tab;
    return 'overview';
  });

  // Persist active tab to URL for deep links.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (activeTab && activeTab !== 'overview') params.set('tab', activeTab);
        else params.delete('tab');
        return params;
      },
      { replace: true }
    );
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    const stepId = analyticsTourApi.step?.id;
    if (stepId === 'details-tab') setActiveTab('details');
    if (stepId === 'estimation-tab') setActiveTab('estimation');
  }, [analyticsTourApi.step?.id]);

  const [estimationData, setEstimationData] = useState<EstimationAccuracyResponse | null>(null);
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [estimationError, setEstimationError] = useState<string | null>(null);
  const [estimationDetail, setEstimationDetail] = useState<EstimationAccuracyDetailResponse | null>(
    null
  );
  const [estimationDetailLoading, setEstimationDetailLoading] = useState(false);
  const [estimationDetailError, setEstimationDetailError] = useState<string | null>(null);
  const [totalTimeUnit, setTotalTimeUnit] = useState<'days' | 'hours'>('days');
  const [totalDoneIssuesFromDb, setTotalDoneIssuesFromDb] = useState<number>(0);
  const [doneCountLoaded, setDoneCountLoaded] = useState(false);
  const [doneStats, setDoneStats] = useState<AnalyticsDoneCountResponse | null>(null);
  const [summaryStats, setSummaryStats] = useState<{
    totalHours: number;
    uniqueAuthors: number;
    avgHoursPerDoneIssueWithWorklog: string;
  } | null>(null);
  const [chartSeries, setChartSeries] = useState<Awaited<
    ReturnType<typeof ApiService.getAnalyticsChartSeries>
  > | null>(null);
  const [chartSeriesLoading, setChartSeriesLoading] = useState(false);
  const [chartSeriesError, setChartSeriesError] = useState<string | null>(null);
  const detailsLoadAttemptedRef = useRef(false);
  const estimationDetailSkipNextFetchRef = useRef<string | null>(null);

  const [issueComments, setIssueComments] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem('issueComments') || '{}');
    } catch {
      return {};
    }
  });
  const [commentPopup, setCommentPopup] = useState<{ key: string; summary: string } | null>(null);
  const [commentDraft, setCommentDraft] = useState('');

  const dateFilterRangeRef = useRef(dateFilterRange);
  dateFilterRangeRef.current = dateFilterRange;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  useEffect(() => {
    localStorage.setItem('issueComments', JSON.stringify(issueComments));
  }, [issueComments]);

  const openCommentPopup = (key: string, summary: string) => {
    setCommentDraft(issueComments[key] ?? '');
    setCommentPopup({ key, summary });
  };

  const saveComment = () => {
    if (!commentPopup) return;
    setIssueComments((prev) => {
      const next = { ...prev };
      if (commentDraft.trim()) {
        next[commentPopup.key] = commentDraft;
      } else {
        delete next[commentPopup.key];
      }
      return next;
    });
    setCommentPopup(null);
  };

  const closeCommentPopup = () => setCommentPopup(null);

  const loadAllTimeDoneCount = useCallback(async () => {
    setDoneCountLoaded(false);
    try {
      const { totalDoneIssues } = await ApiService.getTotalDoneIssues();
      setTotalDoneIssuesFromDb(totalDoneIssues);
    } catch {
      setTotalDoneIssuesFromDb(0);
    } finally {
      setDoneCountLoaded(true);
    }
  }, []);

  const doneStatsRequestIdRef = useRef(0);

  const loadDoneStats = useCallback(async (params?: AnalyticsDateScope) => {
    const requestId = ++doneStatsRequestIdRef.current;
    try {
      const s = await ApiService.getTotalDoneIssues(params);
      if (requestId !== doneStatsRequestIdRef.current) return;
      setDoneStats(s);
    } catch {
      if (requestId !== doneStatsRequestIdRef.current) return;
      setDoneStats({ totalDoneIssues: 0, totalHoursDone: 0, byType: [] });
    }
  }, []);

  const orgOnboardingComplete = isOrganizationOnboardingComplete(currentOrg);
  const { spotlightActive, dismissSpotlight } = useFullUpdateSpotlight({
    orgOnboardingComplete,
    doneCount: totalDoneIssuesFromDb,
    doneCountLoaded,
  });

  const {
    currentStep: postUpdateStep,
    advance: advancePostUpdateJourney,
    skip: skipPostUpdateJourney,
    dismissCurrentStep: dismissPostUpdateStep,
  } = usePostUpdateJourney();

  const lastPostUpdateStepRef = useRef<PostUpdateJourneyStep | null>(null);

  const chartRequestIdRef = useRef(0);

  const summaryRequestIdRef = useRef(0);

  const loadSummaryStats = useCallback(async (params?: AnalyticsDateScope) => {
    const requestId = ++summaryRequestIdRef.current;
    try {
      const s = await ApiService.getAnalyticsSummaryStats(params);
      if (requestId !== summaryRequestIdRef.current) return;
      setSummaryStats(s);
    } catch {
      if (requestId !== summaryRequestIdRef.current) return;
      setSummaryStats(null);
    }
  }, []);

  const loadChartSeries = useCallback(
    async (params?: { startDate?: string; endDate?: string; fixVersion?: string }) => {
      const requestId = ++chartRequestIdRef.current;
      setChartSeriesLoading(true);
      setChartSeriesError(null);
      try {
        const s = await ApiService.getAnalyticsChartSeries(params);
        if (requestId !== chartRequestIdRef.current) return;
        setChartSeries(s);
      } catch (err) {
        if (requestId !== chartRequestIdRef.current) return;
        setChartSeries(null);
        setChartSeriesError(err instanceof Error ? err.message : 'Failed to load chart data');
      } finally {
        if (requestId === chartRequestIdRef.current) {
          setChartSeriesLoading(false);
        }
      }
    },
    []
  );

  const loadEstimationAccuracy = useCallback(async () => {
    setEstimationLoading(true);
    setEstimationError(null);
    estimationDetailSkipNextFetchRef.current = null;
    try {
      const res = await ApiService.getEstimationAccuracy({ includeInitialDetail: true });
      setEstimationData(res);
      if (res.initialDetail != null && res.byRelease?.length) {
        setEstimationDetail(res.initialDetail);
        setEstimationDetailError(null);
        estimationDetailSkipNextFetchRef.current = `release:${res.byRelease[0].period}`;
      } else {
        setEstimationDetail(null);
      }
    } catch (err) {
      setEstimationError(err instanceof Error ? err.message : 'Failed to load estimation accuracy');
    } finally {
      setEstimationLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'estimation') {
      loadEstimationAccuracy();
    }
  }, [activeTab, loadEstimationAccuracy]);

  // Derive estimation view mode from the unified period. Release/Quarter modes
  // reuse the byRelease/byQuarter tables; other modes degrade to daterange.
  const estimationView: 'release' | 'quarter' | 'daterange' = useMemo(() => {
    if (period?.mode === 'release') return 'release';
    if (period?.mode === 'quarter') return 'quarter';
    return 'daterange';
  }, [period]);

  const selectedPeriodLabel = useMemo(() => {
    if (period?.mode === 'release') return period.fixVersion;
    if (period?.mode === 'quarter') return period.quarter;
    return '';
  }, [period]);

  const customStartDate = useMemo(() => {
    if (period?.mode === 'dateRange') return period.startDate;
    if (period?.mode === 'sprint' || period?.mode === 'quarter' || period?.mode === 'release') {
      return resolvedPeriod?.startDate ?? '';
    }
    return '';
  }, [period, resolvedPeriod]);

  const customEndDate = useMemo(() => {
    if (period?.mode === 'dateRange') return period.endDate;
    if (period?.mode === 'sprint' || period?.mode === 'quarter' || period?.mode === 'release') {
      return resolvedPeriod?.endDate ?? '';
    }
    return '';
  }, [period, resolvedPeriod]);

  const loadEstimationDetail = useCallback(async () => {
    if (!estimationData) return;
    if (estimationView === 'release' || estimationView === 'quarter') {
      if (!selectedPeriodLabel) return;
    } else {
      if (!customStartDate || !customEndDate) return;
      if (customStartDate > customEndDate) return;
    }
    const primed = estimationDetailSkipNextFetchRef.current;
    if (
      primed &&
      estimationView === 'release' &&
      selectedPeriodLabel &&
      primed === `release:${selectedPeriodLabel}`
    ) {
      estimationDetailSkipNextFetchRef.current = null;
      return;
    }
    estimationDetailSkipNextFetchRef.current = null;
    setEstimationDetailLoading(true);
    setEstimationDetailError(null);
    try {
      const res =
        estimationView === 'daterange'
          ? await ApiService.getEstimationAccuracyDetail('daterange', {
              startDate: customStartDate,
              endDate: customEndDate,
            })
          : await ApiService.getEstimationAccuracyDetail(
              estimationView as EstimationDetailPeriodType,
              {
                period: selectedPeriodLabel,
              }
            );
      setEstimationDetail(res);
    } catch (err) {
      setEstimationDetailError(err instanceof Error ? err.message : 'Failed to load period detail');
      setEstimationDetail(null);
    } finally {
      setEstimationDetailLoading(false);
    }
  }, [estimationData, estimationView, selectedPeriodLabel, customStartDate, customEndDate]);

  useEffect(() => {
    if (estimationView === 'daterange') {
      if (customStartDate && customEndDate && customStartDate <= customEndDate) {
        loadEstimationDetail();
      } else {
        setEstimationDetail(null);
      }
    } else if (selectedPeriodLabel) {
      loadEstimationDetail();
    } else if (!estimationDetailSkipNextFetchRef.current) {
      setEstimationDetail(null);
    }
  }, [estimationView, selectedPeriodLabel, customStartDate, customEndDate, loadEstimationDetail]);

  // Load (or reload) when the active product changes
  useEffect(() => {
    if (!currentProduct?.id) return;
    const dr = dateFilterRangeRef.current;
    const scope = analyticsScopeFromRange(dr);
    loadAllData();
    loadAllTimeDoneCount();
    loadDoneStats(scope);
    loadSummaryStats(scope);
    loadChartSeries(scope);
    if (dr?.startDate && dr?.endDate) {
      loadDataByDateRange(dr);
      loadWorkLogPage(1, dr);
    } else {
      loadWorkLogPage(1);
    }
    setEstimationDetail(null);
    setEstimationDetailError(null);
    setEstimationData(null);
    if (activeTabRef.current === 'estimation') {
      loadEstimationAccuracy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct?.id]);

  // When the period changes, re-fetch filtered charts + table.
  useEffect(() => {
    const scope = analyticsScopeFromRange(dateFilterRange);
    if (dateFilterRange) {
      loadDataByDateRange(dateFilterRange);
      loadWorkLogPage(1, dateFilterRange);
      loadChartSeries(scope);
      loadSummaryStats(scope);
      loadDoneStats(scope);
    } else {
      loadSummaryStats();
      loadDoneStats();
      loadAllData();
      loadWorkLogPage(1);
      loadChartSeries();
    }
    // `loadDataByDateRange` / `loadAllData` / `loadWorkLogPage` are stable
    // callbacks from useWorkLogData; including them would trigger re-fetch on
    // every render. We intentionally depend only on the resolved range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterRange?.startDate, dateFilterRange?.endDate, dateFilterRange?.fixVersion]);

  useEffect(() => {
    if (!spotlightActive) return;
    databaseSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const id = window.requestAnimationFrame(() => {
      fullUpdateCoachRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [spotlightActive]);

  useEffect(() => {
    if (postUpdateStep === 'dateFilter' && activeTab === 'estimation') {
      setActiveTab('overview');
    }
  }, [postUpdateStep, activeTab]);

  useEffect(() => {
    if (postUpdateStep === 'tabDetails' && lastPostUpdateStepRef.current !== 'tabDetails') {
      setActiveTab('overview');
    }
    if (postUpdateStep === 'tabEstimation' && lastPostUpdateStepRef.current !== 'tabEstimation') {
      setActiveTab('overview');
    }
    lastPostUpdateStepRef.current = postUpdateStep;
  }, [postUpdateStep]);

  useEffect(() => {
    if (postUpdateStep === 'tabDetails' && activeTab === 'details') {
      advancePostUpdateJourney();
    }
  }, [postUpdateStep, activeTab, advancePostUpdateJourney]);

  useEffect(() => {
    if (postUpdateStep === 'tabEstimation' && activeTab === 'estimation') {
      advancePostUpdateJourney();
    }
  }, [postUpdateStep, activeTab, advancePostUpdateJourney]);

  useEffect(() => {
    if (!postUpdateStep) return;
    const id = window.requestAnimationFrame(() => {
      postUpdateJourneyCoachRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [postUpdateStep]);

  useEffect(() => {
    if (postUpdateStep === 'dateFilter') {
      advancePostUpdateJourney();
    }
  }, [postUpdateStep, advancePostUpdateJourney]);

  useEffect(() => {
    setFilteredData(Array.isArray(data) ? data : []);
  }, [data]);

  // Load first page when switching to Details tab
  useEffect(() => {
    if (activeTab !== 'details') {
      detailsLoadAttemptedRef.current = false;
      return;
    }
    loadWorkLogPage(1, dateFilterRange ?? undefined);
    detailsLoadAttemptedRef.current = true;
  }, [activeTab, dateFilterRange, loadWorkLogPage]);

  useEffect(() => {
    if (
      activeTab === 'details' &&
      !detailsLoadAttemptedRef.current &&
      tableTotal === 0 &&
      !tableLoading
    ) {
      detailsLoadAttemptedRef.current = true;
      loadWorkLogPage(1, dateFilterRange ?? undefined);
    }
  }, [activeTab, tableTotal, tableLoading, dateFilterRange, loadWorkLogPage]);

  // Calculate statistics (guard against undefined from initial/async state)
  const safeFilteredData = Array.isArray(filteredData) ? filteredData : [];
  const useServerSummary = summaryStats !== null;
  const useServerCharts = chartSeries !== null;
  const overviewChartsBusy = activeTab === 'overview' && chartSeriesLoading;
  const overviewLoadError = chartSeriesError;
  const overviewChartData: WorkLogEntry[] = useServerCharts ? [] : safeFilteredData;

  const showAnalyticsSetupGuide =
    activeTab === 'overview' &&
    !overviewChartsBusy &&
    !overviewLoadError &&
    !dateFilterRange &&
    summaryStats !== null &&
    summaryStats.totalHours === 0 &&
    totalDoneIssuesFromDb === 0 &&
    !setupStatus.loading;

  const showUnresolvedPeriodWarning =
    period?.mode === 'release' && !dateFilterRange && !overviewChartsBusy;

  const clientTotalHours = safeFilteredData.reduce((sum, entry) => sum + entry.timeSpent, 0);
  const totalHours = useServerSummary ? summaryStats.totalHours : clientTotalHours;
  const totalDays = totalHours / 8;
  const uniqueDevelopers = useServerSummary
    ? summaryStats.uniqueAuthors
    : new Set(safeFilteredData.map((entry) => entry.author)).size;

  const doneEntries = safeFilteredData.filter((entry) => entry.status === 'Done');
  const hoursByKey: Record<string, number> = {};
  doneEntries.forEach((entry) => {
    hoursByKey[entry.key] = (hoursByKey[entry.key] || 0) + entry.timeSpent;
  });
  const doneKeysWithTime = Object.entries(hoursByKey).filter(([, h]) => h > 0);
  const clientHoursDone = doneKeysWithTime.reduce((sum, [, h]) => sum + h, 0);
  const totalIssuesDone = doneStats?.totalDoneIssues ?? 0;
  const avgPerIssue =
    doneStats && doneStats.totalDoneIssues > 0
      ? (doneStats.totalHoursDone / doneStats.totalDoneIssues).toFixed(1)
      : useServerSummary
        ? summaryStats.avgHoursPerDoneIssueWithWorklog
        : doneKeysWithTime.length > 0
          ? (clientHoursDone / doneKeysWithTime.length).toFixed(1)
          : '0';

  return (
    <div className="analytics-page">
      {/* Page Header */}
      <div className="analytics-header">
        <div className="header-content">
          <div className="header-title">
            <BarChart3 className="header-icon" />
            <div>
              <h1>Analytics Dashboard</h1>
              <p>Comprehensive Jira worklog analysis and insights</p>
            </div>
          </div>
        </div>
      </div>

      <GuidedTourCard
        mission={analyticsMission}
        api={analyticsTourApi}
        renderStepBody={(id) => (id === 'connect' ? <AnalyticsConnectTourBody /> : null)}
        onBeforeAdvance={(stepId) => {
          if (stepId === 'calendar-setup') navigate('/calendar');
        }}
        renderIdle={(api) =>
          api.completed || api.skipped ? (
            <button
              type="button"
              className="atlas-tour-restart-link forecast-tour-restart-link"
              onClick={api.restart}
            >
              Restart guided tour
            </button>
          ) : null
        }
      />

      {!atlasAnalyticsActive && postUpdateStep === 'navigateReleases' ? (
        <div ref={postUpdateJourneyCoachRef} tabIndex={-1}>
          <FeatureCoach
            featureKey="journey.navigateReleases"
            productId={productId}
            theme={theme}
            show
            title="Plan your releases"
            role="status"
            aria-live="polite"
            primaryButtonLabel="Open Releases"
            onPrimaryButtonClick={() => navigate('/releases')}
            skipButtonLabel="Skip"
            onSkipButtonClick={skipPostUpdateJourney}
          >
            <>
              Track release scope, sprint capacity, and team allocation across releases. Use{' '}
              <strong>Open Releases</strong> or the Releases link in the header.
            </>
          </FeatureCoach>
        </div>
      ) : null}

      {/* Statistics Cards */}
      <div className="stats-cards">
        <DoneIssuesStatCard
          totalDoneIssues={totalIssuesDone}
          byType={doneStats?.byType ?? []}
          periodActive={Boolean(dateFilterRange)}
        />

        <div className="stat-card-item">
          <div className="stat-icon stat-icon-blue">
            <Clock className="w-6 h-6" />
          </div>
          <div className="stat-details">
            <span className="stat-label">
              {totalTimeUnit === 'days' ? 'Total (days)' : 'Total Hours'}
              <span
                className="stat-hint-icon"
                title="Total time logged in worklogs. Toggle between days (8h = 1 day) and hours. Depends on the current Period."
                aria-label="Hint"
              >
                <Info size={14} />
              </span>
            </span>
            <div className="stat-value-row">
              <span className="stat-value">
                {totalTimeUnit === 'days' ? totalDays.toFixed(1) : totalHours.toFixed(1)}
                {totalTimeUnit === 'days' ? ' d' : ' h'}
              </span>
              <button
                type="button"
                className="stat-unit-toggle"
                onClick={() => setTotalTimeUnit((u) => (u === 'days' ? 'hours' : 'days'))}
                title={totalTimeUnit === 'days' ? 'Show in hours' : 'Show in days'}
                aria-label={totalTimeUnit === 'days' ? 'Switch to hours' : 'Switch to days'}
              >
                {totalTimeUnit === 'days' ? '→ h' : '→ d'}
              </button>
            </div>
          </div>
        </div>

        <div className="stat-card-item">
          <div className="stat-icon stat-icon-green">
            <Users className="w-6 h-6" />
          </div>
          <div className="stat-details">
            <span className="stat-label">
              Team Members
              <span
                className="stat-hint-icon"
                title="Count of unique authors (display names) who have at least one worklog entry in the current data. Depends on the current Period."
                aria-label="Hint"
              >
                <Info size={14} />
              </span>
            </span>
            <span className="stat-value">{uniqueDevelopers}</span>
          </div>
        </div>

        <div className="stat-card-item">
          <div className="stat-icon stat-icon-orange">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div className="stat-details">
            <span className="stat-label">
              Avg per Issue
              <span
                className="stat-hint-icon"
                title="Average tracked time per issue (in hours) among Done issues in the current scope. The denominator matches Total Issues Done."
                aria-label="Hint"
              >
                <Info size={14} />
              </span>
            </span>
            <span className="stat-value">{avgPerIssue}h</span>
          </div>
        </div>
      </div>

      {/* Database Management (left) + Period picker (right). */}
      <div className="analytics-controls analytics-controls--database-period">
        <div id="analytics-database-anchor" ref={databaseSectionRef} className="control-section">
          <div className="section-title">
            <Tooltip
              position="bottom"
              text="Sync and manage worklog data from Jira. Full Update loads all tasks; Incremental Update only fetches changes since the last full run."
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                <Database className="w-5 h-5" />
                <span>Database Management</span>
              </span>
            </Tooltip>
          </div>
          {spotlightActive ? (
            <div
              ref={fullUpdateCoachRef}
              className="full-update-coach"
              role="status"
              aria-live="polite"
              tabIndex={-1}
              aria-label="Next step: run Full Update to sync Jira data"
            >
              <Sparkles size={18} aria-hidden />
              <span>
                Run <strong>Full Update</strong> to sync your Jira worklogs for the first time.
              </span>
            </div>
          ) : null}
          <DatabaseUpdater
            jiraService={jiraService}
            spotlightActive={spotlightActive}
            onSpotlightDismiss={dismissSpotlight}
            onUpdateComplete={() => {
              const dr = dateFilterRangeRef.current;
              const scope = analyticsScopeFromRange(dr);
              notifyJiraWorklogIssuesSynced();
              loadAllData();
              loadAllTimeDoneCount();
              loadDoneStats(scope);
              loadSummaryStats(scope);
              loadChartSeries(scope);
            }}
          />
        </div>

        <PeriodPicker
          value={period}
          onChange={setPeriod}
          options={periodOptions}
          loading={periodOptionsLoading}
          sprintPickerMeta={sprintPickerMeta}
        />
      </div>

      {/* Tabs Navigation */}
      <div id="analytics-tabs-anchor" className="analytics-tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 className="w-5 h-5" />
          <span>Overview & Charts</span>
        </button>
        <button
          id="analytics-tab-details-anchor"
          type="button"
          className={`tab-btn ${activeTab === 'details' ? 'active' : ''}${
            !atlasAnalyticsActive && postUpdateStep === 'tabDetails'
              ? ' coach-target-highlight'
              : ''
          }`}
          onClick={() => setActiveTab('details')}
        >
          <Download className="w-5 h-5" />
          <span>Detailed Data & Export</span>
        </button>
        <button
          id="analytics-tab-estimation-anchor"
          type="button"
          className={`tab-btn ${activeTab === 'estimation' ? 'active' : ''}${
            !atlasAnalyticsActive && postUpdateStep === 'tabEstimation'
              ? ' coach-target-highlight'
              : ''
          }`}
          onClick={() => setActiveTab('estimation')}
        >
          <Target className="w-5 h-5" />
          <span>Estimation accuracy</span>
        </button>
        <button
          id="analytics-tab-quality-anchor"
          type="button"
          className={`tab-btn ${activeTab === 'quality' ? 'active' : ''}`}
          onClick={() => setActiveTab('quality')}
          title="Bug volume, buggy components and labels, verification failures, and QA dynamics. All metrics respect the current Period."
        >
          <ShieldAlert className="w-5 h-5" />
          <span>Quality</span>
        </button>
      </div>

      {!atlasAnalyticsActive && postUpdateStep === 'tabDetails' ? (
        <div ref={postUpdateJourneyCoachRef} tabIndex={-1}>
          <FeatureCoach
            featureKey="journey.tabDetails"
            productId={productId}
            theme={theme}
            show
            title="Explore your detailed data"
            role="status"
            aria-live="polite"
            onDismiss={dismissPostUpdateStep}
          >
            <>
              The <strong>Detailed Data</strong> tab lists every worklog entry and lets you export
              to CSV or Excel.
            </>
          </FeatureCoach>
        </div>
      ) : null}
      {!atlasAnalyticsActive && postUpdateStep === 'tabEstimation' ? (
        <div ref={postUpdateJourneyCoachRef} tabIndex={-1}>
          <FeatureCoach
            featureKey="journey.tabEstimation"
            productId={productId}
            theme={theme}
            show
            title="Check estimation accuracy"
            role="status"
            aria-live="polite"
            onDismiss={dismissPostUpdateStep}
          >
            <>Compare estimated vs. actual time per issue — spot patterns and improve planning.</>
          </FeatureCoach>
        </div>
      ) : null}

      {/* Content Area — wrapped in EstimationUnitProvider so the Hours/Days
       toggle is shared across every analytics card that displays time
       (Estimation accuracy tab + Quality MTTR / By Bug Type cards). */}
      <EstimationUnitProvider>
        <div className="analytics-content">
          {activeTab === 'overview' && overviewLoadError && (
            <div className="error-state">
              <p>Error: {overviewLoadError}</p>
            </div>
          )}

          {activeTab === 'overview' && overviewChartsBusy && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading charts…</p>
            </div>
          )}

          {activeTab === 'overview' && !overviewChartsBusy && !overviewLoadError && (
            <div className="charts-section">
              {showUnresolvedPeriodWarning && (
                <div
                  className="analytics-setup-guide estimation-empty-state"
                  style={{ marginBottom: '1.5rem', gridColumn: '1 / -1' }}
                  role="alert"
                  aria-label="Date range could not be resolved for selected release"
                >
                  <p className="estimation-empty-title">Release date range unavailable</p>
                  <p className="estimation-empty-text">
                    The selected release (<strong>{period?.fixVersion}</strong>) does not have a
                    matching entry in the Release Calendar, and the fallback date range is too wide
                    to display meaningful data. Charts are showing unfiltered data for the product.
                  </p>
                  <p className="estimation-empty-text" style={{ marginTop: '0.5rem' }}>
                    To fix this, add this version to the <a href="/releases">Release Calendar</a>{' '}
                    with start and end dates, or select a different period.
                  </p>
                </div>
              )}
              {showAnalyticsSetupGuide ? (
                <div
                  className="analytics-setup-guide estimation-empty-state"
                  style={{ marginBottom: '1.5rem', gridColumn: '1 / -1' }}
                  role="region"
                  aria-label="Getting started with analytics data"
                >
                  <p className="estimation-empty-title">No worklog data yet</p>
                  <p className="estimation-empty-text">
                    Follow these steps once Jira is connected — then charts and tables will
                    populate.
                  </p>
                  <ol className="estimation-empty-steps">
                    <li>
                      <strong>Connect Jira</strong>
                      {setupStatus.jiraConnected ? (
                        <span style={{ opacity: 0.85 }}> — done</span>
                      ) : (
                        <>
                          {' '}
                          — <a href="/settings?tab=project">Open Jira settings</a>
                        </>
                      )}
                    </li>
                    <li>
                      <strong>Run a Full Update</strong> in Database Management below to sync
                      worklogs from Jira.
                    </li>
                    <li>
                      <strong>Check your team</strong>
                      {setupStatus.teamMembersCount > 0 ? (
                        <span style={{ opacity: 0.85 }}>
                          {' '}
                          — {setupStatus.teamMembersCount} member(s) in roster
                        </span>
                      ) : (
                        <>
                          {' '}
                          — <a href="/settings?tab=team">Open team settings</a>
                        </>
                      )}
                    </li>
                  </ol>
                  <div
                    style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}
                  >
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() =>
                        databaseSectionRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        })
                      }
                    >
                      Go to Database Management
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setupStatus.refresh()}
                    >
                      Recheck setup
                    </button>
                  </div>
                </div>
              ) : null}
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Daily Time Distribution</h3>
                  <p>Visual representation of time spent per day</p>
                </div>
                <div className="chart-content">
                  <DailyTimeChart
                    data={overviewChartData}
                    precomputedDaily={useServerCharts ? chartSeries?.daily : undefined}
                  />
                </div>
              </div>

              <div className="chart-card">
                <div className="chart-header chart-header--with-toolbar">
                  <div className="chart-header__main">
                    <h3>Team Member Time Distribution</h3>
                    <p>Time distribution across different team members</p>
                  </div>
                  <EstimationUnitToggle />
                </div>
                <div className="chart-content">
                  <DeveloperTimeChart
                    data={overviewChartData}
                    precomputedByAuthorType={
                      useServerCharts ? chartSeries?.byAuthorType : undefined
                    }
                  />
                </div>
              </div>

              <div className="chart-card">
                <div className="chart-header chart-header--with-toolbar">
                  <div className="chart-header__main">
                    <h3>Task Type Distribution</h3>
                    <p>Unique issues with worklog in the current Period (not worklog rows)</p>
                  </div>
                  <EstimationUnitToggle />
                </div>
                <div className="chart-content">
                  <TaskTypeDistributionChart
                    data={overviewChartData}
                    precomputedByType={useServerCharts ? chartSeries?.byType : undefined}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="details-section">
              {error && (
                <div className="error-state" style={{ marginBottom: '1rem' }}>
                  <p>Error loading table: {error}</p>
                </div>
              )}
              <WorkLogTable
                data={tableEntries ?? []}
                totalCount={tableTotal}
                page={tablePage}
                pageSize={tablePageSize}
                onPageChange={(p) => loadWorkLogPage(p, dateFilterRange ?? undefined)}
                onPageSizeChange={(size) => {
                  setTablePageSize(size);
                  loadWorkLogPage(1, dateFilterRange ?? undefined);
                }}
                pageSizeOptions={tablePageSizeOptions}
                loading={tableLoading}
                onExportCSV={async () => {
                  const all = dateFilterRange
                    ? await jiraService.getWorkLogEntriesByDateRange(dateFilterRange)
                    : await jiraService.getAllWorkLogEntries();
                  jiraService.exportToCSV(all);
                }}
                onExportExcel={async () => {
                  const all = dateFilterRange
                    ? await jiraService.getWorkLogEntriesByDateRange(dateFilterRange)
                    : await jiraService.getAllWorkLogEntries();
                  jiraService.exportToExcel(all);
                }}
              />
            </div>
          )}

          {activeTab === 'estimation' && (
            <div id="estimation-accuracy-section-anchor" className="estimation-accuracy-section">
              {estimationLoading && (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading estimation accuracy...</p>
                </div>
              )}
              {estimationError && (
                <div className="error-state">
                  <p>Error: {estimationError}</p>
                </div>
              )}
              {!estimationLoading && !estimationError && estimationData && (
                <>
                  {estimationData.kpi?.totalStoriesWithEstimate === 0 && (
                    <div className="estimation-empty-state">
                      <p className="estimation-empty-title">No estimation data yet</p>
                      <p className="estimation-empty-text">
                        This view shows <strong>Stories</strong> that have both worklog time and
                        <strong> Original time estimate</strong> in Jira.
                      </p>
                      <ol className="estimation-empty-steps">
                        <li>
                          In Jira: set <strong>Original time estimate</strong> (or Time estimate) on
                          your Stories.
                        </li>
                        <li>
                          Here: run <strong>Database Management → Update database</strong> to
                          re-sync worklog from Jira (so estimates are stored).
                        </li>
                        <li>Reopen this tab or refresh the page.</li>
                      </ol>
                    </div>
                  )}
                  {((estimationData.byRelease?.length ?? 0) > 0 ||
                    (estimationData.byQuarter?.length ?? 0) > 0) && (
                    <EstimationTrendCard
                      data={estimationData}
                      targetDeviationPct={targetDeviationPct}
                    />
                  )}
                  <div className="estimation-kpi-cards">
                    <div className="stat-card-item estimation-kpi">
                      <div className="stat-icon stat-icon-green">
                        <Target className="w-6 h-6" />
                      </div>
                      <div className="stat-details">
                        <span className="stat-label">
                          Last {estimationData.kpi?.lastReleasesCount ?? 0} releases: within ±
                          {targetDeviationPct}%
                        </span>
                        <span className="stat-value">
                          {estimationData.kpi?.lastReleasesWithin10Percent != null
                            ? `${estimationData.kpi?.lastReleasesWithin10Percent}%`
                            : '—'}
                        </span>
                      </div>
                    </div>
                    <div className="stat-card-item estimation-kpi">
                      <div className="stat-icon stat-icon-purple">
                        <TrendingUp className="w-6 h-6" />
                      </div>
                      <div className="stat-details">
                        <span className="stat-label">Stories with estimate (total)</span>
                        <span className="stat-value">
                          {estimationData.kpi?.totalStoriesWithEstimate ?? 0}
                        </span>
                      </div>
                    </div>
                    <div className="stat-card-item estimation-kpi estimation-kpi-target">
                      <div className="stat-details">
                        <span className="stat-label">Target: deviation ≤{targetDeviationPct}%</span>
                        <span className="stat-value stat-value-muted">KPI reference</span>
                      </div>
                    </div>
                  </div>

                  {(estimationDetailLoading || estimationDetailError || estimationDetail) && (
                    <div className="estimation-detail-block">
                      {estimationDetailLoading && (
                        <div className="loading-state estimation-detail-loading">
                          <div className="spinner"></div>
                          <p>Loading period detail...</p>
                        </div>
                      )}
                      {estimationDetailError && (
                        <div className="error-state">
                          <p>{estimationDetailError}</p>
                        </div>
                      )}
                      {!estimationDetailLoading &&
                        !estimationDetailError &&
                        estimationDetail &&
                        estimationDetail.summary && (
                          <EstimationDetailBlock
                            detail={estimationDetail}
                            issueComments={issueComments}
                            openCommentPopup={openCommentPopup}
                            targetDeviationPct={targetDeviationPct}
                          />
                        )}
                    </div>
                  )}

                  {estimationView !== 'daterange' && (
                    <EstimationByPeriodTable
                      rows={
                        estimationView === 'release'
                          ? (estimationData.byRelease ?? [])
                          : (estimationData.byQuarter ?? [])
                      }
                      view={estimationView === 'release' ? 'release' : 'quarter'}
                      targetDeviationPct={targetDeviationPct}
                    />
                  )}
                </>
              )}
              {!estimationLoading && !estimationError && !estimationData && (
                <div className="error-state">
                  <p>No estimation accuracy data available.</p>
                  <p className="estimation-hint">
                    Ensure Stories in Jira have &quot;Original time estimate&quot; set and worklog
                    data is synced.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'quality' && (
            <QualityTab period={period} resolvedPeriod={resolvedPeriod} />
          )}
        </div>
      </EstimationUnitProvider>
      {commentPopup && (
        <div
          className="issue-comment-overlay"
          onClick={closeCommentPopup}
          role="dialog"
          aria-modal="true"
          aria-label="Issue note"
        >
          <div className="issue-comment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="issue-comment-modal__header">
              <div className="issue-comment-modal__title">
                <MessageSquare size={16} />
                <span>Note for</span>
                <a
                  href={`${JIRA_BROWSE}${encodeURIComponent(commentPopup.key)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="task-link"
                >
                  {commentPopup.key}
                </a>
              </div>
              <button
                type="button"
                className="issue-comment-modal__close"
                onClick={closeCommentPopup}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <p className="issue-comment-modal__summary" title={commentPopup.summary}>
              {commentPopup.summary}
            </p>
            <textarea
              className="issue-comment-modal__textarea"
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              placeholder="Write your analysis note here…"
              rows={5}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveComment();
                if (e.key === 'Escape') closeCommentPopup();
              }}
            />
            <div className="issue-comment-modal__footer">
              <span className="issue-comment-modal__hint">Ctrl+Enter to save · Esc to cancel</span>
              <div className="issue-comment-modal__actions">
                <button type="button" className="btn btn-secondary" onClick={closeCommentPopup}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={saveComment}>
                  Save note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
